import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import DataTable from "@/components/DataTable";
import StatusBadge from "@/components/StatusBadge";
import { Plus, GraduationCap } from "lucide-react";

const CAN_CREATE_FORMATION = ["ADMINISTRATEUR", "HSE", "RESPONSABLE_HSE", "CHEF_PROJET", "FORMATEUR"];

const ETAT_COLOR = {
  BROUILLON: "#6B7280", A_PREPARER: "#B45309", SOUS_CONDITIONS: "#D97706",
  PRETE: "#15803D", EN_COURS: "#0E7490", SUSPENDUE: "#C2410C", TERMINEE: "#374151",
};

function EtatCell({ e }) {
  if (!e) return <span style={{ color: "var(--text-muted)", fontSize: ".75rem" }}>…</span>;
  const c = ETAT_COLOR[e.etat] || "#6B7280";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
      <span style={{ padding: "2px 8px", borderRadius: 20, background: `${c}1a`, color: c, fontWeight: 700, fontSize: ".72rem" }}>{e.etatLabel}</span>
      {e.nbBloquants > 0 && <span style={{ color: "#DC2626", fontSize: ".72rem", fontWeight: 600 }}>· {e.nbBloquants} bloquant{e.nbBloquants > 1 ? "s" : ""}</span>}
    </span>
  );
}

export default function Operations() {
  const { selectedSite, labels } = useApp();
  const { user } = useAuth();
  const [ops, setOps] = useState([]);
  const [etats, setEtats] = useState({});
  const nav = useNavigate();
  const canFormation = CAN_CREATE_FORMATION.includes(user?.role);

  useEffect(() => {
    const q = selectedSite !== "all" ? `?siteId=${selectedSite}` : "";
    api.get(`/operations${q}`).then((r) => {
      setOps(r.data);
      const ids = r.data.map((o) => o.id);
      if (ids.length) api.post("/operations/etats", { ids }).then((res) => setEtats(res.data)).catch(() => {});
    });
  }, [selectedSite]);

  const columns = [
    { key: "id", label: "Réf.", mono: true, width: 130 },
    { key: "titre", label: "Opération", render: (r) => <b>{r.titre}</b> },
    { key: "typeOperation", label: "Type", width: 110,
      filter: [{ value: "reelle", label: "Réelle" }, { value: "formation", label: "Formation" }],
      render: (r) => r.typeOperation === "formation"
        ? <span className="tag" style={{ background: "var(--ambre, #E49B1A)", color: "#fff" }}>Formation</span>
        : <span className="tag">Réelle</span> },
    { key: "siteNom", label: "Site" },
    { key: "eeNom", label: "Entreprise ext." },
    { key: "criticite", label: "Criticité", width: 120,
      filter: Object.entries(labels.criticite).map(([value, label]) => ({ value, label })),
      render: (r) => <StatusBadge value={r.criticite} label={labels.criticite[r.criticite]} /> },
    { key: "dateDebut", label: "Début", mono: true, width: 110 },
    { key: "etatMetier", label: "Préparation", width: 190, sortable: false,
      render: (r) => <EtatCell e={etats[r.id]} /> },
    { key: "statut", label: "Statut", width: 120,
      filter: Object.entries(labels.statutOp).map(([value, label]) => ({ value, label })),
      render: (r) => <StatusBadge value={r.statut} label={labels.statutOp[r.statut]} /> },
  ];

  return (
    <div>
      <div className="page-head">
        <div><div className="crumb">Registre</div><h1>Opérations</h1></div>
        <div style={{ display: "flex", gap: 10 }}>
          {canFormation && (
            <button className="btn" style={{ background: "var(--ambre, #E49B1A)", color: "#fff" }}
              onClick={() => nav("/operations/new?type=formation")} data-testid="new-formation-btn">
              <GraduationCap size={15} />Nouvelle opération de formation
            </button>
          )}
          <button className="btn btn-p" onClick={() => nav("/operations/new")} data-testid="new-operation-btn"><Plus size={15} />Nouvelle opération</button>
        </div>
      </div>
      <DataTable testid="operations-table" columns={columns} data={ops}
        searchKeys={["id", "titre", "siteNom", "eeNom"]} onRowClick={(r) => nav(`/operations/${r.id}`)}
        emptyText="Aucune opération. Créez la première."
        cardRender={(r) => (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <span className="mono" style={{ fontSize: ".72rem", color: "var(--text-muted)" }}>{r.id}</span>
              <StatusBadge value={r.statut} label={labels.statutOp[r.statut]} />
            </div>
            <div style={{ fontWeight: 700, margin: "4px 0 2px" }}>{r.titre}</div>
            <div style={{ fontSize: ".8rem", color: "var(--text-muted)" }}>{r.siteNom}{r.eeNom ? ` · ${r.eeNom}` : ""}</div>
            <div style={{ marginTop: 8 }}><EtatCell e={etats[r.id]} /></div>
          </>
        )} />
    </div>
  );
}
